/**
 * useModuloCrm — o CRM está LIGADO no portal de acesso?
 *
 * Liga/desliga por portal (decisão de 22/10/2026), gerido na ServicosHabilitadosPage:
 *
 *     servicoshabilitados/{domainDetails.domainId}.servicos.crm.habilitado === true
 *
 * OPT-IN e FAIL-CLOSED, igual ao backend (functions/middleware/gateCrmPortal.js):
 * sem documento, sem a chave, ou erro de leitura → `ativo: false`. Este hook só
 * decide o que MOSTRAR (rotas, menus); quem barra de verdade é a API.
 *
 * A chave é `crm`, NÃO `tarefas`: `servicos.tarefas` sobrou da Module Federation
 * (removida em 23/10/2026), tinha versão pinada e outro significado — ninguém a lê.
 *
 * Suíte E2E com auth simulada (VITE_E2E_MOCK_AUTH=true): o payload do
 * localStorage pode trazer `servicosHabilitados` (o mesmo mapa `servicos` do
 * documento) e ele é usado NO LUGAR da leitura do Firestore — aquela suíte não
 * tem token e não deve ler produção. Ver e2e-crm/helpers/perfil.js. Em build
 * normal a flag é falsa e esse ramo nem existe.
 *
 * @returns {{ carregando: boolean, ativo: boolean }}
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GenericService from '../services/Generic.service';
import { carregarServicosDoc } from '../services/servicosHabilitados';

export const SERVICO_CRM = 'crm';

/** Decisão pura (exportada para teste): só `habilitado === true` liga. */
export function crmHabilitadoNoDoc(servicosDoc) {
  return servicosDoc?.servicos?.[SERVICO_CRM]?.habilitado === true;
}

const E2E_MOCK_AUTH = import.meta.env.VITE_E2E_MOCK_AUTH === 'true';

function servicosDoMockE2E() {
  if (!E2E_MOCK_AUTH || typeof window === 'undefined') return undefined;
  try {
    const payload = JSON.parse(window.localStorage.getItem('__SIE_E2E_AUTH_MOCK__') || 'null');
    return payload && payload.servicosHabilitados ? { servicos: payload.servicosHabilitados } : undefined;
  } catch (_) {
    return undefined;
  }
}

// O megamenu, a guarda das rotas e o botão do cadastro perguntam a mesma coisa na
// mesma navegação: uma leitura por (portal, usuário) a cada 30s basta. O uid entra
// na chave porque a leitura exige login — a resposta de antes do login (negada pelas
// Security Rules → "desligado") não pode valer depois dele.
const TTL_MS = 30 * 1000;
const cache = new Map(); // `${domainId}|${uid}` -> { em, promessa }

function lerServicosDoPortal(domainId, uid) {
  const doMock = servicosDoMockE2E();
  if (doMock !== undefined) return Promise.resolve(doMock);
  const chave = `${domainId}|${uid || ''}`;
  const guardado = cache.get(chave);
  if (guardado && Date.now() - guardado.em < TTL_MS) return guardado.promessa;
  const promessa = carregarServicosDoc(domainId, (c, id, silent) => GenericService.getOne(c, id, silent));
  cache.set(chave, { em: Date.now(), promessa });
  return promessa;
}

/** Esquece as leituras guardadas (ex.: a página administrativa acabou de ligar/desligar). */
export function esquecerModuloCrm() {
  cache.clear();
}

export function useModuloCrm() {
  const auth = useAuth() || {};
  const domainId = auth.domainDetails?.domainId || null;
  const uid = auth.currentUser?.uid || null;
  const chave = `${domainId}|${uid}`;
  const [estado, setEstado] = useState({ carregando: true, ativo: false, chave: null });

  useEffect(() => {
    let vivo = true;
    if (!domainId) {
      // Portal ainda não resolvido: continua "carregando" (não decide desligado cedo
      // demais — redirecionaria quem só abriu a página antes do AuthContext assentar).
      return undefined;
    }
    lerServicosDoPortal(domainId, uid).then((doc) => {
      if (vivo) setEstado({ carregando: false, ativo: crmHabilitadoNoDoc(doc), chave: `${domainId}|${uid}` });
    });
    return () => { vivo = false; };
  }, [domainId, uid]);

  // Trocou de portal/usuário e a leitura nova ainda não voltou: não reaproveita a
  // decisão anterior (nem para ligar, nem para desligar).
  if (!domainId || estado.chave !== chave) return { carregando: true, ativo: false };
  return { carregando: estado.carregando, ativo: estado.ativo };
}

export default useModuloCrm;
